/**
 * Hook SIMPLES para rateio - versão KISS
 *
 * Uma única função que faz tudo de forma direta
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { usePrenotaStore } from "@PN/inclusao";
import { useCatalogs } from "store";
import { toast } from "react-hot-toast";
import type { RateioFixoItem } from "@apiInclui/rateioFixo/types";

export function useRateio() {
  const draft = usePrenotaStore((state) => state.draft);
  const aux = usePrenotaStore((state) => state.aux);
  const setRateios = usePrenotaStore((state) => state.actions.draft.setRateios);
  const { filiais, centroCusto } = useCatalogs();

  // Estado para verificar se há rateio fixo disponível
  const [temRateioFixo, setTemRateioFixo] = useState(false);
  
  // Estado para modo de importação do Excel: 'valor' ou 'percentual'
  const [modoImportacao, setModoImportacao] = useState<'valor' | 'percentual'>('valor');

  // Verifica se é despesa e tem apenas um produto
  const isDespesaComUmProduto = useMemo(() => {
    return (
      draft.opcoes?.tipoNota === "Despesa/Imobilizado" &&
      draft.produtos.length === 1
    );
  }, [draft.opcoes?.tipoNota, draft.produtos.length]);

  // Verifica se há rateio fixo disponível quando for despesa com um produto
  useEffect(() => {
    const verificarRateioFixo = async () => {
      if (!isDespesaComUmProduto) {
        setTemRateioFixo(false);
        return;
      }

      const produto = draft.produtos[0];
      const codProduto = produto?.codProduto?.trim();
      const filial = draft.filial?.trim();

      if (!codProduto || !filial) {
        setTemRateioFixo(false);
        return;
      }

      try {
        const response = await fetch(
          `/api/prenotas/rateioFixo?codProduto=${encodeURIComponent(codProduto)}&filial=${encodeURIComponent(filial)}`
        );

        if (!response.ok) {
          setTemRateioFixo(false);
          return;
        }

        const result = await response.json();
        const temDados = result.success && result.data && result.data.length > 0;
        console.log("[RATEIO FIXO] Verificação:", { codProduto, filial, temDados, result });
        console.log("[RATEIO FIXO] setTemRateioFixo chamado com:", temDados);
        setTemRateioFixo(temDados);
      } catch (error) {
        setTemRateioFixo(false);
      }
    };

    verificarRateioFixo();
  }, [isDespesaComUmProduto, draft.produtos, draft.filial]);

  // Função para normalizar código de filial (aceita apenas 3 ou 4 dígitos)
  const normalizeFilialCode = useCallback((code: string): string => {
    const cleanCode = code.trim();

    // Se já tem 4 dígitos, retorna como está
    if (cleanCode.length === 4) {
      return cleanCode;
    }

    // Se tem 3 dígitos, adiciona zero na frente
    if (cleanCode.length === 3) {
      return `0${cleanCode}`;
    }

    // Códigos com menos de 3 dígitos são inválidos
    return cleanCode; // Retorna o código original para que a validação falhe
  }, []);

  // Função para validar se filial existe
  const validateFilial = useCallback(
    (
      filialCode: string
    ): { isValid: boolean; normalizedCode?: string; filial?: any } => {
      const normalizedCode = normalizeFilialCode(filialCode);
      const filial = filiais.getBy("codigo", normalizedCode);

      return {
        isValid: !!filial,
        normalizedCode,
        filial,
      };
    },
    [filiais, normalizeFilialCode]
  );

  // Função para validar se centro de custo existe para a filial
  const validateCentroCusto = useCallback(
    (filialCode: string, ccCode: string): { isValid: boolean; cc?: any } => {
      const normalizedFilial = normalizeFilialCode(filialCode);
      const ccOptions =
        centroCusto.getOptionsByFilial?.(normalizedFilial) || [];
      const cc = ccOptions.find((option) => option.value === ccCode);

      return {
        isValid: !!cc,
        cc,
      };
    },
    [centroCusto, normalizeFilialCode]
  );

  // ✅ Usa o boolean centralizado da store
  const temRegrasEspeciais = aux.temRegrasEspeciais;

  // Cálculos simples
  const calculations = useMemo(() => {
    // ✅ Fonte única da verdade: usa total líquido da store quando disponível
    let totalGeral = 0;
    if (
      aux.totalNota &&
      typeof aux.totalNota === "object" &&
      (aux.totalNota as any).total &&
      (aux.totalNota as any).total !== "0"
    ) {
      totalGeral =
        Number(String((aux.totalNota as any).total).replace(",", ".")) || 0;
    } else {
      // Fallback: soma dos produtos do draft
      totalGeral = draft.produtos.reduce(
        (sum, p) => sum + (p.valorTotal || 0),
        0
      );
    }
    const totalRateado = draft.rateios.reduce(
      (sum, r) => sum + (r.valor || 0),
      0
    );
    const restante = Math.max(0, totalGeral - totalRateado);
    const percentualRateado =
      totalGeral > 0 ? (totalRateado / totalGeral) * 100 : 0;
    const isCompleto = Math.abs(totalRateado - totalGeral) < 0.01;

    return {
      totalGeral,
      totalRateado,
      restante,
      percentualRateado,
      isCompleto,
    };
  }, [draft.produtos, draft.rateios, aux.totalNota]);

  // Adicionar rateio
  const addRateio = useCallback(
    (data: any) => {
      if (temRegrasEspeciais && draft.rateios.length > 0) {
        toast.error(
          "Tipos com regras especiais permitem apenas um rateio de 100%"
        );
        return;
      }

      if (
        !data.filial ||
        !data.centroCusto ||
        (!temRegrasEspeciais && data.valor < 0.01)
      ) {
        toast.error("Preencha todos os campos");
        return;
      }

      // Valida filial
      const filialValidation = validateFilial(data.filial);
      if (!filialValidation.isValid) {
        toast.error(`Filial "${data.filial}" não encontrada`);
        return;
      }

      // Valida centro de custo
      const ccValidation = validateCentroCusto(data.filial, data.centroCusto);
      if (!ccValidation.isValid) {
        toast.error(
          `Centro de custo "${data.centroCusto}" não encontrado para a filial "${filialValidation.normalizedCode}"`
        );
        return;
      }

      const novoRateio = {
        ...data,
        filial: filialValidation.normalizedCode!, // Usa o código normalizado
        valor: temRegrasEspeciais ? calculations.totalGeral : data.valor,
        percentual: temRegrasEspeciais
          ? 100
          : (data.valor / calculations.totalGeral) * 100,
        rec: 0,
      };

      setRateios([...draft.rateios, novoRateio]);
      toast.success("Rateio adicionado!");
    },
    [
      draft.rateios,
      setRateios,
      temRegrasEspeciais,
      calculations.totalGeral,
      validateFilial,
      validateCentroCusto,
    ]
  );

  // Remover rateio
  const removeRateio = useCallback(
    (index: number) => {
      const novosRateios = draft.rateios.filter((_, i) => i !== index);
      setRateios(novosRateios);
      toast.success("Rateio removido!");
    },
    [draft.rateios, setRateios]
  );

  // Limpar todos os rateios
  const clearAllRateios = useCallback(() => {
    setRateios([]);
    toast.success("Todos os rateios foram removidos!");
  }, [setRateios]);

  // Editar rateio
  const editRateio = useCallback(
    (index: number, data: any) => {
      if (
        !data.filial ||
        !data.centroCusto ||
        (!temRegrasEspeciais && data.valor < 0.01)
      ) {
        toast.error("Preencha todos os campos");
        return false;
      }

      // Valida filial
      const filialValidation = validateFilial(data.filial);
      if (!filialValidation.isValid) {
        toast.error(`Filial "${data.filial}" não encontrada`);
        return false;
      }

      // Valida centro de custo
      const ccValidation = validateCentroCusto(data.filial, data.centroCusto);
      if (!ccValidation.isValid) {
        toast.error(
          `Centro de custo "${data.centroCusto}" não encontrado para a filial "${filialValidation.normalizedCode}"`
        );
        return false;
      }

      const novosRateios = [...draft.rateios];
      novosRateios[index] = {
        ...data,
        filial: filialValidation.normalizedCode!, // Usa o código normalizado
        valor: temRegrasEspeciais ? calculations.totalGeral : data.valor,
        percentual: temRegrasEspeciais
          ? 100
          : (data.valor / calculations.totalGeral) * 100,
        rec: 0,
      };

      setRateios(novosRateios);
      toast.success("Rateio editado!");
      return true;
    },
    [
      draft.rateios,
      setRateios,
      temRegrasEspeciais,
      calculations.totalGeral,
      validateFilial,
      validateCentroCusto,
    ]
  );

  // Processar Excel
  const processExcel = useCallback(
    async (clipboardText: string) => {
      const lines = clipboardText.split("\n").filter((line) => line.trim());
      const novosRateios = [];

      for (const line of lines) {
        // Tenta diferentes separadores: tab, ponto e vírgula, pipe, vírgula
        const separators = [/\t/, /;/, /\|/, /,/];
        let parts: string[] = [];

        for (const sep of separators) {
          parts = line.split(sep);
          if (parts.length >= 3) {
            break;
          }
        }

        if (parts.length < 3) {
          continue;
        }

        const [filial, centroCusto, valorOuPercentual] = parts;

        if (filial && centroCusto && valorOuPercentual) {
          // Valida filial
          const filialValidation = validateFilial(filial.trim());

          if (!filialValidation.isValid) {
            continue;
          }

          // Valida centro de custo
          const ccValidation = validateCentroCusto(
            filial.trim(),
            centroCusto.trim()
          );
          if (!ccValidation.isValid) {
            continue;
          }

          // Limpa o valor/percentual removendo R$, %, espaços e convertendo vírgula para ponto
          const dadoLimpo = valorOuPercentual
            .replace(/R\$\s?/g, "")
            .replace(/%/g, "")
            .replace(/\s/g, "")
            .replace(",", ".");

          const dadoNum = parseFloat(dadoLimpo);

          if (dadoNum > 0) {
            let valor: number;
            let percentual: number;

            // Processa baseado no modo selecionado
            if (modoImportacao === 'percentual') {
              // Modo percentual: o dado colado é o percentual
              percentual = dadoNum;
              valor = calculations.totalGeral > 0 
                ? (percentual / 100) * calculations.totalGeral 
                : 0;
            } else {
              // Modo valor: o dado colado é o valor
              valor = dadoNum;
              percentual = calculations.totalGeral > 0
                ? (valor / calculations.totalGeral) * 100
                : 0;
            }

            const novoRateio = {
              filial: filialValidation.normalizedCode!, // Usa o código normalizado
              centroCusto: centroCusto.trim(),
              valor: temRegrasEspeciais ? calculations.totalGeral : valor,
              percentual: temRegrasEspeciais ? 100 : percentual,
              rec: 0,
            };

            novosRateios.push(novoRateio);
          }
        }
      }

      if (novosRateios.length > 0) {
        const novosRateiosFinais = temRegrasEspeciais
          ? novosRateios
          : [...draft.rateios, ...novosRateios];

        setRateios(novosRateiosFinais);

        const totalLinhas = lines.length;
        const linhasIgnoradas = totalLinhas - novosRateios.length;

        if (linhasIgnoradas > 0) {
          toast.success(
            `${novosRateios.length} rateio(s) importado(s)! ${linhasIgnoradas} linha(s) ignorada(s) (filial/CC inválidos)`
          );
        } else {
          toast.success(`${novosRateios.length} rateio(s) importado(s)!`);
        }
      } else {
        toast.error(
          "Nenhum rateio válido encontrado nos dados colados. Verifique se as filiais e centros de custo existem."
        );
      }
    },
    [
      draft.rateios,
      setRateios,
      temRegrasEspeciais,
      calculations.totalGeral,
      validateFilial,
      validateCentroCusto,
      modoImportacao,
    ]
  );

  // Aplica rateio fixo quando o botão for clicado
  const aplicarRateioFixo = useCallback(async () => {
    // Só aplica se for despesa com um produto
    if (!isDespesaComUmProduto) {
      return;
    }

    const produto = draft.produtos[0];
    const codProduto = produto?.codProduto?.trim();
    const filial = draft.filial?.trim();

    if (!codProduto || !filial) {
      toast.error("Código do produto ou filial não encontrado");
      return;
    }

    try {
      const response = await fetch(
        `/api/prenotas/rateioFixo?codProduto=${encodeURIComponent(codProduto)}&filial=${encodeURIComponent(filial)}`
      );

      if (!response.ok) {
        toast.error("Erro ao buscar rateio fixo");
        return;
      }

      const result = await response.json();
      if (!result.success || !result.data || result.data.length === 0) {
        toast.error("Nenhum rateio fixo encontrado para este produto");
        return;
      }

      const rateiosFixos: RateioFixoItem[] = result.data;
      const totalGeral = calculations.totalGeral;

      // Converte rateios fixos para o formato esperado
      const novosRateios = rateiosFixos.map((rateioFixo) => {
        // Calcula o valor baseado no percentual do rateio fixo
        const valor = totalGeral > 0 
          ? (totalGeral * rateioFixo.percentual) / 100 
          : 0;

        return {
          filial: rateioFixo.filial,
          centroCusto: rateioFixo.centroCusto,
          valor,
          percentual: rateioFixo.percentual,
          rec: 0,
        };
      });

      // Aplica os rateios fixos
      setRateios(novosRateios);
      toast.success(`${novosRateios.length} rateio(s) fixo(s) aplicado(s)!`);
    } catch (error) {
      toast.error("Erro ao aplicar rateio fixo");
      console.error("[RATEIO FIXO] Erro ao aplicar rateio fixo:", error);
    }
  }, [
    isDespesaComUmProduto,
    draft.produtos,
    draft.filial,
    calculations.totalGeral,
    setRateios,
  ]);

  return {
    rateios: draft.rateios,
    calculations,
    temRegrasEspeciais,
    filiaisOptions: filiais.options,
    centroCustoOptions: centroCusto.getOptionsByFilial || (() => []),
    addRateio,
    removeRateio,
    clearAllRateios,
    editRateio,
    processExcel,
    validateFilial,
    validateCentroCusto,
    normalizeFilialCode,
    aplicarRateioFixo,
    isDespesaComUmProduto,
    temRateioFixo,
    modoImportacao,
    setModoImportacao,
  };
}
